/**
 * AutoLISP app bridge (P3+P4) — the primitive layer that connects LISP to
 * the app: (command ...) feeding through the shared dispatcher and interact
 * engine, get* prompts as single-step InteractionSpecs, transcript output,
 * and the entmake/entget/ssget entity API over the cad: ledger. Registered at engine
 * bootstrap (core/lisp/index.ts); the dispatcher only ever talks to
 * registry.ts, never to this module.
 */

import * as THREE from 'three';
import { LispError } from './errors';
import {
    NIL,
    mkEname,
    mkInt,
    mkList,
    mkPickset,
    mkReal,
    mkStr,
    printValue,
    strVal,
    type LispValue,
} from './values';
import type { LispRuntime } from './interpreter';
import { dispatchCommand } from '../CommandDispatcher';
import { useCommandInteract } from '../commands/commandInteract';
import { injectPointAtWorld, setAnchorPoint, resolveDynamicPoint } from '../dynamicInput/dynamicInputEngine';
import { useAppStore, type Entity } from '../../state/store';
import { notify } from '../../utils/notify';
import { undoableAdd, undoableDelete, undoableModify, makeEntity } from '../commands/entityOps';
import { getSystemVariable, setSystemVariable } from './sysvars';
import {
    assocListToEntityBuild,
    cadEntities,
    entityToAssocList,
    selectByWindow,
} from './entityMap';
import { define } from './stdlib/internal';

// ---------------------------------------------------------------------------
// Points and prompts
// ---------------------------------------------------------------------------

const vec = (p: THREE.Vector3) => `${p.x.toFixed(4)}, ${p.y.toFixed(4)}`;

/** World point → AutoLISP point list (x y z). */
const pointToLisp = (p: THREE.Vector3): LispValue =>
    mkList([mkReal(p.x), mkReal(p.y), mkReal(p.z)]);

function pointFromLisp(v: LispValue): THREE.Vector3 {
    if (v.k === 'list' && v.items.length >= 2 && v.items.length <= 3) {
        const nums = v.items.map((item) => (item.k === 'int' || item.k === 'real' ? item.v : NaN));
        if (nums.every((n) => Number.isFinite(n))) {
            return new THREE.Vector3(nums[0], nums[1], nums[2] ?? 0);
        }
    }
    throw new LispError('bad argument type: expected a point (list of 2-3 reals)');
}

function requireNoActiveInteract(op: string): void {
    if (useCommandInteract.getState().active) {
        throw new LispError(`${op}: another command is waiting for input`);
    }
}

/** One point step collected through the shared interact engine. */
function collectPoint(prompt: string): Promise<THREE.Vector3> {
    return new Promise((resolve, reject) => {
        useCommandInteract.getState().start({
            steps: [{ prompt, kind: 'point' }],
            complete: (points) => {
                resolve(points[0] ?? new THREE.Vector3());
                return points[0] ? vec(points[0]) : '';
            },
            cancel: () => reject(new LispError('Function cancelled')),
        });
    });
}

function collectTwoPoints(first: string, second: string): Promise<[THREE.Vector3, THREE.Vector3]> {
    return new Promise((resolve, reject) => {
        useCommandInteract.getState().start({
            steps: [
                { prompt: first, kind: 'point' },
                { prompt: second, kind: 'point' },
            ],
            complete: (points) => {
                resolve([points[0], points[1]]);
                return `${vec(points[0])} → ${vec(points[1])}`;
            },
            cancel: () => reject(new LispError('Function cancelled')),
        });
    });
}

function collectValue(prompt: string, kind: 'text' | 'numeric'): Promise<string | number> {
    return new Promise((resolve, reject) => {
        useCommandInteract.getState().start({
            steps: [{ prompt, kind }],
            complete: (_points, values) => {
                const value = values[0];
                resolve(typeof value === 'number' ? value : String(value ?? ''));
                return printRaw(value);
            },
            cancel: () => reject(new LispError('Function cancelled')),
        });
    });
}

function printRaw(value: number | string | undefined): string {
    if (value === undefined) return '';
    return typeof value === 'number' ? String(value) : value;
}

// ---------------------------------------------------------------------------
// initget keyword state (consumed by the next get* call)
// ---------------------------------------------------------------------------

let pendingInitget: { bits: number; keywords: string[] } | null = null;

function consumeInitget(): { bits: number; keywords: string[] } {
    const pending = pendingInitget;
    pendingInitget = null;
    return pending ?? { bits: 0, keywords: [] };
}

// ---------------------------------------------------------------------------
// (command ...) argument feeding
// ---------------------------------------------------------------------------

/** Tool scopes mount one React render after activation; give them a frame. */
async function settleViewport(): Promise<void> {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => setTimeout(resolve, 40));
}

async function feedPoint(rt: LispRuntime, point: THREE.Vector3): Promise<void> {
    const ci = useCommandInteract.getState();
    if (ci.active && ci.spec) {
        const step = ci.spec.steps[ci.stepIndex];
        if (!step || step.kind !== 'point') {
            throw new LispError('command: current prompt does not take a point');
        }
        ci.addPoint(point);
        setAnchorPoint(point);
        return;
    }
    const tool = useAppStore.getState().activeTool;
    if (!tool || tool === 'select') {
        throw new LispError('command: no active command to receive the point');
    }
    await settleViewport();
    if (!injectPointAtWorld(point)) {
        throw new LispError('command: viewport is not ready');
    }
}

async function feedEnter(): Promise<void> {
    const ci = useCommandInteract.getState();
    if (ci.active) {
        ci.finishActive();
        return;
    }
    // Active tool hook: the hooks finish on a window Enter keydown.
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
}

async function feedValue(value: number | string): Promise<void> {
    const ci = useCommandInteract.getState();
    if (ci.active && ci.spec) {
        const step = ci.spec.steps[ci.stepIndex];
        if (!step || (step.kind !== 'numeric' && step.kind !== 'text')) {
            throw new LispError('command: current prompt does not take a value');
        }
        ci.provideValue(value);
        return;
    }
    // Tool hook with a coordinate-shaped string: inject as a point.
    if (typeof value === 'string') {
        const resolved = resolveDynamicPoint(value);
        if (resolved && 'point' in resolved) {
            const tool = useAppStore.getState().activeTool;
            if (tool && tool !== 'select') {
                await settleViewport();
                if (injectPointAtWorld(resolved.point)) {
                    setAnchorPoint(resolved.point);
                    return;
                }
            }
        }
    }
    throw new LispError('command: no active command to receive the value');
}

/** PAUSE: wait for one interact step (or tool progress) driven by the user. */
async function pauseForUser(rt: LispRuntime): Promise<void> {
    const ci = useCommandInteract.getState();
    if (ci.active) {
        const startIndex = ci.stepIndex;
        await new Promise<void>((resolve) => {
            const unsub = useCommandInteract.subscribe((s) => {
                if (!s.active || s.stepIndex !== startIndex) {
                    unsub();
                    resolve();
                }
            });
            if (!useCommandInteract.getState().active) {
                unsub();
                resolve();
            }
        });
        return;
    }
    // Active tool hook: no step observable — wait until the tool deactivates.
    const startTool = useAppStore.getState().activeTool;
    for (;;) {
        if (rt.isCancelled()) throw new LispError('Function cancelled');
        await new Promise<void>((resolve) => setTimeout(resolve, 100));
        if (useAppStore.getState().activeTool !== startTool) return;
    }
}

function makeCommandSubr(throwOnUnknown: boolean) {
    return async (rt: LispRuntime, args: LispValue[]): Promise<LispValue> => {
        const nameVal = args[0];
        if (!nameVal || nameVal.k !== 'str' || !nameVal.v.trim()) {
            throw new LispError('bad argument type: expected a command name string');
        }
        const name = nameVal.v.trim();
        rt.out(`Command: ${name}`);
        const result = dispatchCommand(name);
        if (!result.success) {
            if (throwOnUnknown) throw new LispError(result.message);
            // (command ...): an unknown command skips its arguments, the
            // script continues — AutoCAD behavior.
            rt.out(`; error: ${result.message}`);
            return NIL;
        }
        if (result.message) rt.out(result.message);
        for (const arg of args.slice(1)) {
            if (arg.k === 'sym' && arg.name === 'PAUSE') {
                await pauseForUser(rt);
                continue;
            }
            if (arg.k === 'str' && arg.v === '') {
                await feedEnter();
                continue;
            }
            if (arg.k === 'list') {
                await feedPoint(rt, pointFromLisp(arg));
                continue;
            }
            if (arg.k === 'int' || arg.k === 'real') {
                await feedValue(arg.v);
                continue;
            }
            if (arg.k === 'str') {
                await feedValue(arg.v);
                continue;
            }
            if (arg.k === 'ename') {
                throw new LispError('command: ename arguments are not supported yet');
            }
            throw new LispError('bad argument type');
        }
        return NIL;
    };
}

// ---------------------------------------------------------------------------
// Entity API (P4)
// ---------------------------------------------------------------------------

function entityByEname(v: LispValue, fn: string): Entity {
    if (v.k !== 'ename') throw new LispError(`${fn}: bad argument type (expected an ename)`);
    const entity = useAppStore.getState().entities[v.id];
    if (!entity) throw new LispError(`${fn}: entity no longer exists`);
    return entity;
}

function entityOrEname(id: string): LispValue {
    return mkEname(id);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/** Install the app bridge primitives on the runtime's global environment. */
export function registerAppApi(rt: LispRuntime): void {
    // ---- Output ----------------------------------------------------------
    define(rt, 'princ', (args) => {
        rt.out(args.map((a) => printValue(a, true)).join(''));
        return args.length > 0 ? args[args.length - 1] : NIL;
    });
    define(rt, 'prin1', (args) => {
        rt.out(args.map((a) => printValue(a, false)).join(''));
        return args.length > 0 ? args[args.length - 1] : NIL;
    });
    define(rt, 'print', (args) => {
        rt.out(args.map((a) => printValue(a, false)).join(' '));
        return args.length > 0 ? args[args.length - 1] : NIL;
    });
    define(rt, 'prompt', (args) => {
        rt.out(strVal(args[0] ?? NIL));
        return NIL;
    });
    define(rt, 'terpri', () => {
        rt.out('');
        return NIL;
    });
    define(rt, 'alert', (args) => {
        notify(strVal(args[0] ?? NIL), 'info');
        return NIL;
    });

    // ---- (command ...) / (command-s ...) ---------------------------------
    rt.globalEnv.define('command', {
        k: 'subr',
        name: 'command',
        fn: (args: LispValue[]) => makeCommandSubr(false)(rt, args),
    });
    rt.globalEnv.define('command-s', {
        k: 'subr',
        name: 'command-s',
        fn: (args: LispValue[]) => makeCommandSubr(true)(rt, args),
    });

    // ---- get* input primitives -------------------------------------------
    define(rt, 'getpoint', async (args) => {
        requireNoActiveInteract('getpoint');
        consumeInitget();
        const msg = args[0] && args[0].k === 'str' ? args[0].v : '';
        return pointToLisp(await collectPoint(msg || 'getpoint: pick a point'));
    });
    define(rt, 'getcorner', async (args) => {
        requireNoActiveInteract('getcorner');
        const base = args[0] ? pointFromLisp(args[0]) : null;
        const point = await collectPoint(base ? `getcorner: opposite corner (from ${vec(base)})` : 'getcorner: pick opposite corner');
        return pointToLisp(point);
    });
    define(rt, 'getdist', async () => {
        requireNoActiveInteract('getdist');
        const [a, b] = await collectTwoPoints('getdist: first point', 'getdist: second point');
        return mkReal(a.distanceTo(b));
    });
    define(rt, 'getangle', async () => {
        requireNoActiveInteract('getangle');
        const [a, b] = await collectTwoPoints('getangle: vertex point', 'getangle: angle endpoint');
        return mkReal(Math.atan2(b.y - a.y, b.x - a.x));
    });
    define(rt, 'getorient', async () => {
        requireNoActiveInteract('getorient');
        const [a, b] = await collectTwoPoints('getorient: vertex point', 'getorient: angle endpoint');
        return mkReal(Math.atan2(b.y - a.y, b.x - a.x));
    });
    define(rt, 'getstring', async (args) => {
        requireNoActiveInteract('getstring');
        consumeInitget();
        const msg = args[0] && args[0].k === 'str' ? args[0].v : '';
        const value = await collectValue(msg || 'getstring: enter text', 'text');
        return mkStr(String(value));
    });
    define(rt, 'getint', async () => {
        requireNoActiveInteract('getint');
        const value = await collectValue('getint: enter an integer', 'numeric');
        const n = typeof value === 'number' ? value : Number(String(value).trim());
        if (!Number.isFinite(n)) throw new LispError('getint: a number is required');
        return mkInt(Math.trunc(n));
    });
    define(rt, 'getreal', async () => {
        requireNoActiveInteract('getreal');
        const value = await collectValue('getreal: enter a value', 'numeric');
        const n = typeof value === 'number' ? value : Number(String(value).trim());
        if (!Number.isFinite(n)) throw new LispError('getreal: a number is required');
        return mkReal(n);
    });
    define(rt, 'getkword', async (_args, runtime) => {
        requireNoActiveInteract('getkword');
        const { keywords } = consumeInitget();
        if (keywords.length === 0) {
            throw new LispError('getkword: no keywords — call (initget bits "KW1 KW2 ...") first');
        }
        for (;;) {
            const value = await collectValue('getkword: enter a keyword', 'text');
            const text = String(value).trim().toUpperCase();
            const match = keywords.find((kw) => kw.toUpperCase() === text);
            if (match) return mkStr(match.toUpperCase());
            runtime.out(`Requires one of: ${keywords.join(', ')}.`);
        }
    });
    define(rt, 'initget', (args) => {
        const bits = args[0] && (args[0].k === 'int' || args[0].k === 'real') ? Math.trunc(args[0].v) : 0;
        const keywords = args[1] && args[1].k === 'str'
            ? args[1].v.trim().split(/\s+/).filter(Boolean)
            : [];
        pendingInitget = { bits, keywords };
        return NIL;
    });

    // ---- system variables -------------------------------------------------
    rt.globalEnv.define('getvar', {
        k: 'subr',
        name: 'getvar',
        fn: (args: LispValue[]) => {
            const name = args[0];
            if (!name || name.k !== 'str') throw new LispError('getvar: bad argument type');
            return getSystemVariable(name.v);
        },
    });
    rt.globalEnv.define('setvar', {
        k: 'subr',
        name: 'setvar',
        fn: (args: LispValue[]) => {
            const name = args[0];
            if (!name || name.k !== 'str') throw new LispError('setvar: bad argument type');
            setSystemVariable(name.v, args[1] ?? NIL);
            return mkStr(name.v);
        },
    });

    // ---- window/text screen ----------------------------------------------
    define(rt, 'textscr', () => {
        window.dispatchEvent(new CustomEvent('yap:text-window', { detail: { open: true } }));
        return NIL;
    });
    define(rt, 'graphscr', () => {
        window.dispatchEvent(new CustomEvent('yap:text-window', { detail: { open: false } }));
        return NIL;
    });

    // ---- entity API --------------------------------------------------------
    rt.globalEnv.define('entmake', {
        k: 'subr',
        name: 'entmake',
        fn: (args: LispValue[]) => {
            const build = assocListToEntityBuild(args[0] ?? NIL);
            const entity = makeEntity(build.type, build.name, build.data, build.layerId);
            const [id] = undoableAdd([entity]);
            return entityOrEname(id);
        },
    });
    define(rt, 'entlast', () => {
        const all = cadEntities();
        return all.length > 0 ? entityOrEname(all[all.length - 1].id) : NIL;
    });
    define(rt, 'entnext', (args: LispValue[]) => {
        const all = cadEntities();
        if (!args[0] || args[0].k === 'nil') {
            return all.length > 0 ? entityOrEname(all[0].id) : NIL;
        }
        const current = entityByEname(args[0], 'entnext');
        const idx = all.findIndex((e) => e.id === current.id);
        return idx >= 0 && idx + 1 < all.length ? entityOrEname(all[idx + 1].id) : NIL;
    });
    define(rt, 'entprev', (args: LispValue[]) => {
        const all = cadEntities();
        if (!args[0] || args[0].k === 'nil') {
            return all.length > 0 ? entityOrEname(all[all.length - 1].id) : NIL;
        }
        const current = entityByEname(args[0], 'entprev');
        const idx = all.findIndex((e) => e.id === current.id);
        return idx > 0 ? entityOrEname(all[idx - 1].id) : NIL;
    });
    define(rt, 'entget', (args: LispValue[]) => entityToAssocList(entityByEname(args[0] ?? NIL, 'entget')));
    define(rt, 'entdel', (args: LispValue[]) => {
        const entity = entityByEname(args[0] ?? NIL, 'entdel');
        undoableDelete([entity.id]);
        return entityOrEname(entity.id);
    });
    define(rt, 'entmod', (args: LispValue[]) => {
        const pairs = args[0];
        if (!pairs || pairs.k !== 'list') throw new LispError('entmod: bad argument type');
        // The handle (group 5) or -1 (ename) identifies the entity to modify.
        let targetId: string | null = null;
        for (const item of pairs.items) {
            if (item.k !== 'list' || item.items.length < 2) continue;
            const code = item.items[0].k === 'int' ? item.items[0].v : NaN;
            const value = item.dot ?? item.items[1];
            if (code === 5 && value.k === 'str') targetId = `cad:${value.v}`;
            if (code === -1 && value.k === 'ename') targetId = value.id;
        }
        if (!targetId) throw new LispError('entmod: missing entity handle (group 5 or -1)');
        const app = useAppStore.getState();
        const entity = app.entities[targetId];
        if (!entity) throw new LispError('entmod: entity no longer exists');
        const build = assocListToEntityBuild(pairs);
        if (build.type !== entity.type) throw new LispError('entmod: entity type mismatch');
        undoableModify([targetId], (data) => ({ ...data, ...build.data }));
        return entityOrEname(targetId);
    });
    define(rt, 'entupd', (args: LispValue[]) => entityOrEname(entityByEname(args[0] ?? NIL, 'entupd').id));
    define(rt, 'handent', (args: LispValue[]) => {
        const handle = strVal(args[0] ?? NIL);
        if (!handle) return NIL;
        const entity = useAppStore.getState().entities[`cad:${handle}`];
        return entity ? entityOrEname(entity.id) : NIL;
    });

    // ---- selection sets ----------------------------------------------------
    define(rt, 'ssget', async (args: LispValue[]) => {
        const mode = args[0] && args[0].k === 'str' ? args[0].v.trim().toUpperCase() : '';
        if (!mode) {
            // No mode: adopt the current selection (documented simplification).
            const selected = useAppStore.getState().selectedIds;
            return selected.length > 0 ? mkPickset([...selected]) : NIL;
        }
        if (mode === 'L') {
            const all = cadEntities();
            return all.length > 0 ? mkPickset([all[all.length - 1].id]) : NIL;
        }
        if (mode === 'P') {
            const selected = useAppStore.getState().selectedIds;
            return selected.length > 0 ? mkPickset([...selected]) : NIL;
        }
        if (mode === 'W' || mode === 'C') {
            const p1 = args[1] ? pointFromLisp(args[1]) : null;
            const p2 = args[2] ? pointFromLisp(args[2]) : null;
            if (!p1 || !p2) throw new LispError(`ssget ${mode}: two corner points are required`);
            const ids = selectByWindow([p1.x, p1.y], [p2.x, p2.y], mode === 'C');
            return ids.length > 0 ? mkPickset(ids) : NIL;
        }
        throw new LispError(`ssget: mode "${mode}" not supported yet (W, C, L, P or none)`);
    });
    define(rt, 'sslength', (args) => {
        const ss = args[0] ?? NIL;
        if (ss.k !== 'pickset') throw new LispError('sslength: bad argument type');
        return mkInt(ss.ids.length);
    });
    define(rt, 'ssname', (args) => {
        const ss = args[0] ?? NIL;
        if (ss.k !== 'pickset') throw new LispError('ssname: bad argument type');
        const idx = args[1] && (args[1].k === 'int' || args[1].k === 'real') ? Math.trunc(args[1].v) : -1;
        const id = ss.ids[idx];
        return id ? entityOrEname(id) : NIL;
    });
    define(rt, 'ssadd', (args) => {
        const base = args[1] ?? args[0];
        const ids = base && base.k === 'pickset' ? [...base.ids] : [];
        if (args[0] && args[0].k === 'ename' && !ids.includes(args[0].id)) ids.push(args[0].id);
        return mkPickset(ids);
    });
    define(rt, 'ssdel', (args) => {
        const ss = args[1] ?? NIL;
        const e = args[0];
        if (ss.k !== 'pickset' || !e || e.k !== 'ename') throw new LispError('ssdel: bad argument type');
        return mkPickset(ss.ids.filter((id) => id !== e.id));
    });
    define(rt, 'ssmemb', (args) => {
        const ss = args[1] ?? NIL;
        const e = args[0];
        if (ss.k !== 'pickset' || !e || e.k !== 'ename') {
            throw new LispError('ssmemb: bad argument type');
        }
        return ss.ids.includes(e.id) ? e : NIL;
    });

    // ---- load (honest redirect) -------------------------------------------
    define(rt, 'load', () => {
        rt.out('LOAD: sandboxed runtime — use APPLOAD to load LISP files.');
        return NIL;
    });
}
